import React, { useEffect, useState } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  styled,
  Box,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchClientsRequest } from '../../redux/client/clientSlice';

export interface ISelectOption {
  value: string;
  id: string;
  label: string;
  profilePic: string;
  firstName: string;
  lastName: string;
}

interface IRenderAvatarProps {
  profilePic: string;
  firstName: string;
  lastName: string;
}

const RenderAvatar = ({ profilePic, firstName, lastName }: IRenderAvatarProps) => {
  if (profilePic) {
    return (
      <img
        src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${profilePic}`}
        alt="user_img"
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          objectFit: 'cover',
        }}
      />
    );
  }
  const initials = firstName[0] + lastName[0];
  return (
    <Box
      sx={{
        borderRadius: '50%',
        backgroundColor: 'secondary.main',
        color: 'white',
        width: '32px',
        height: '32px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {initials.toUpperCase()}
    </Box>
  );
};
const StyledSelect = styled(Select)(() => ({
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&&&:before': {
      borderBottom: 'none',
    },
    '&&:after': {
      borderBottom: 'none',
    },
  },
  '& .MuiSelect-select': {
    backgroundColor: 'rgba(171, 209, 198, 0.20)',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 6px',
    height: '3px',
    '&:focus': {
      borderRadius: '5px',
      backgroundColor: 'rgba(171, 209, 198, 0.20)',
    },
  },
  '& fieldset': {
    border: 'none',
  },
}));

interface IClientsDropdownProps {
  selectedClient: ISelectOption | null;
  handleClientChange: (_selectedClient: ISelectOption) => void;
}

const ClientsDropdown = ({ selectedClient, handleClientChange }: IClientsDropdownProps) => {
  const dispatch = useAppDispatch();
  const [clientOptions, setClientOptions] = useState<ISelectOption[]>([]);
  const clients = useAppSelector((state) => state.client.clients);
  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || '';
  useEffect(() => {
    dispatch(fetchClientsRequest({ coachId }));
  }, []);
  useEffect(() => {
    const options: ISelectOption[] = clients.map((client) => ({
      id: client.id,
      value: client.id,
      label: `${client.firstName} ${client.lastName}`,
      firstName: client.firstName,
      lastName: client.lastName,
      profilePic: client.profilePic,
    }));
    setClientOptions(options);
  }, [clients]);
  return (
    <FormControl fullWidth>
      <StyledSelect
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={clientOptions.find((option) => option.id === selectedClient?.id)?.label || ''}
        inputProps={{ 'aria-label': 'Without label' }}
        name="client"
      >
        {clientOptions.map((option) => (
          <MenuItem key={option.id} value={option.label} onClick={() => handleClientChange(option)}>
            <ListItemIcon
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                maxWidth: '32px',
                width: '32px',
              }}
            >
              {option.profilePic && (
                <img
                  src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${option.profilePic}`}
                  alt={option.label}
                  style={{ width: 32, height: 32 }}
                />
              )}
              {!option.profilePic && (
                <RenderAvatar
                  profilePic={option.profilePic}
                  firstName={option.firstName}
                  lastName={option.lastName}
                />
              )}
            </ListItemIcon>
            <ListItemText
              primary={option.label}
              sx={{
                ml: '10px',
              }}
            />
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

export default ClientsDropdown;
